// Półprodukty: przepis z produktów dający N porcji (np. „Naleśniki" = 7 × naleśnik).
// Do zapisanego posiłku wchodzą w porcjach (`semi_product` + `portions`, bez
// `product` i `weight`), a sumy makr API liczy na żywo.
//
// Wpis dnia zna tylko kopie „nazwa + gramy", więc półprodukt udaje tam produkt
// liczony w porcjach: waga porcji = waga przepisu / liczba porcji, makra na 100 g.
// Dzięki temu te same pickery, pole ilości i „2 × naleśnik (171 g)" działają bez zmian.

import { ref } from 'vue';
import { useApi } from '~/composables/useApi.ts';
import { formatAmount, formatNumber } from '@/composables/usePortions.js';
import { t } from '@/i18n/index.js';

// Wspólny katalog dla całej aplikacji — pobierany raz, odświeżany po zmianach.
const catalog = ref([]);
let pending = null;
let loaded = false;

export function useSemiProducts() {
    const api = useApi();

    const load = async (force = false) => {
        if (loaded && !force) return catalog.value;
        if (pending && !force) return pending;
        pending = api.get('/semi-products')
            .then((list) => {
                catalog.value = list ?? [];
                loaded = true;
                return catalog.value;
            })
            .catch(() => catalog.value)
            .finally(() => { pending = null; });
        return pending;
    };

    return { semiProducts: catalog, loadSemiProducts: load };
}

export const semiPortionName = (semi) => semi?.portion_name?.trim() || t('food.portions.defaultName');

export const semiTotalWeight = (semi) => (semi?.ingredients ?? [])
    .reduce((sum, i) => sum + (Number(i.weight) || 0), 0);

// Wartość na jedną porcję (kcal, makra, sól w mg, cena w zł).
export const perPortion = (semi, key) => {
    const portions = Number(semi?.portions) || 1;
    return (Number(semi?.[key]) || 0) / portions;
};

// Półprodukt jako „produkt" do wpisu dnia. Bez składników nie ma wagi porcji,
// więc takiego nie da się wpisać — zwracamy null.
export const semiAsProduct = (semi) => {
    const total = semiTotalWeight(semi);
    if (!total) return null;
    const per100 = (key) => (Number(semi[key]) || 0) * 100 / total;
    return {
        uuid: semi.uuid,
        name: semi.name,
        semi,
        unit: 'portion',
        portion_weight: total / (Number(semi.portions) || 1),
        portion_name: semiPortionName(semi),
        calories: per100('calories'),
        protein: per100('protein'),
        fat: per100('fat'),
        saturated_fat: per100('saturated_fat'),
        carbs: per100('carbs'),
        sugar: per100('sugar'),
        fiber: per100('fiber'),
        salt: per100('salt'),
        // Cena produktu jest w zł za kilogram.
        price: (Number(semi.price) || 0) * 1000 / total,
    };
};

// Katalog do dopasowania składników wpisu po nazwie: produkty + półprodukty.
export const withSemiProducts = (products, semis) => [
    ...(products ?? []),
    ...(semis ?? []).map(semiAsProduct).filter(Boolean),
];

// Nazwa składnika: kopia we wpisie dnia, produkt albo półprodukt posiłku.
export const ingredientName = (ing) => ing?.product_name ?? ing?.product?.name ?? ing?.semi_product?.name ?? t('food.ingredient');

// Ilość składnika zapisanego posiłku, przemnożona przez liczbę porcji posiłku:
// „2 × naleśnik" dla półproduktu, „2 × sztuka (120 g)" / „120 g" dla produktu.
export const ingredientAmount = (ing, factor = 1) => {
    if (ing?.semi_product) {
        const portions = Math.round((Number(ing.portions) || 0) * factor * 100) / 100;
        return `${formatNumber(portions)} × ${semiPortionName(ing.semi_product)}`;
    }
    if (ing?.weight == null) return '';
    return formatAmount(Math.round(Number(ing.weight) * factor), ing.product);
};

// Gramy składnika-półproduktu do kopii we wpisie dnia (API wpisu wymaga wagi).
// Skrót półproduktu w posiłku nie ma składników — wagę bierzemy z katalogu.
export const semiIngredientGrams = (ing) => {
    const semi = catalog.value.find(s => s.uuid === ing?.semi_product?.uuid);
    const portionWeight = semiTotalWeight(semi) / (Number(semi?.portions) || 1);
    const grams = Math.round(portionWeight * (Number(ing?.portions) || 0));
    // Katalog jeszcze niepobrany albo przepis bez składników — API i tak wymaga > 0.
    return grams > 0 ? grams : 1;
};
